import type {
  FloorPlateCounts,
  TableGridArea,
  TableGridEntry,
} from "@/lib/api/types";
import { checkFreshness } from "./preBillCheck";

/**
 * What one floor tile says about its table, derived from the grid read.
 *
 * The till's floor (`TableGridPage`) computed this inline, tile by tile, and the
 * back office's live view (`FloorLiveView`) computed a coarser copy of it. The
 * PAPER tile is drawn on both screens, so the facts live here once — the older
 * tiles keep their own inline reads untouched.
 *
 * STATUS is the one word the tile leads with, and the colour of its edge —
 * the redesign's words, read off the REAL plate states (2026-10-03). For a
 * table with live bills, the first that holds wins, summed over its bills:
 *
 * 1. `waiting` — a guest sent items from the table that nobody has accepted
 *    yet (D8). Red: the floor's one call to action, because nothing else moves
 *    until a person answers it.
 * 2. `ready` — food is on the pass (any plate READY). Green: someone should
 *    carry it now, whatever else is still cooking. On a dine-in table the
 *    courses go out as they are ready — starters on the pass while the mains
 *    cook are dying there, and this is the runner's only floor signal for it.
 * 3. `preparing` — the kitchen has plates (QUEUED or PREPARING). Blue.
 *    OFF-PREMISE ONLY (B4, D173), `preparing` outranks `ready`: a bag leaves
 *    whole, so «جاهز» on a bag whose second dish still cooks sends the packer
 *    to hand over half an order (`liveStatus(…, offPremise)`). Every tile on
 *    the floor today is a TABLE, so the tile reads dine-in.
 * 4. `eating` — every chargeable plate has reached the table. Purple.
 * 5. `open` — a live bill with nothing fired yet (the basket only, or empty).
 *    The redesign has no word for it; it keeps the till's "Occupied" on a
 *    plain edge.
 *
 * With no live bill: `cheque` (closed, printed, unpaid), else `free`. A live
 * bill outranks a cheque out, as the tile's tap does; the cheque is still
 * reported (`cheque`, `chequeCount`) so the tile can badge it.
 *
 * Against an API from before the plate counts, `ready` and `eating` cannot be
 * told apart from the rest: a table with plates in the kitchen reads
 * `preparing` and any other live table `open`.
 */
export type FloorTileStatus =
  | "free"
  | "waiting"
  | "ready"
  | "preparing"
  | "eating"
  | "open"
  | "cheque";

type OpenInvoice = TableGridEntry["openInvoices"][number];
type ClosedInvoice = TableGridEntry["closedInvoices"][number];

export interface FloorTileFacts {
  status: FloorTileStatus;
  /** The first live bill (by receipt number), or null. */
  open: OpenInvoice | null;
  openBillCount: number;
  /** Summed across every live bill — the tile describes the TABLE. */
  openRunningTotal: number;
  pendingCount: number;
  selfSeated: boolean;
  hasPaymentClaim: boolean;
  /** D120 — a waiter sent an unsent order to the cashier and it is still unsent. */
  cashierRequested: boolean;
  /**
   * D153 — a live bill on this table names no «الكابتن», so the head waiter
   * can fix it before the rush. Only a server that says `hasCaptain: false`
   * counts: an API from before D153 says nothing, and the tile then says
   * nothing either. A bag's tile never reads it (no table, no captain).
   */
  noCaptain: boolean;
  /** The latest cheque out, or null. */
  cheque: ClosedInvoice | null;
  chequeCount: number;
  chequeTotal: number;
  /** When the earliest live bill opened — the table's age. */
  oldestOpenedAt: string | null;
  /**
   * What the table still owes: the live bills' `amountDue` summed (their
   * `runningTotal` on an older API), or the cheques' when only cheques are out.
   */
  due: number;
  /** Paid toward those same bills so far (0 on an older API). */
  paidSoFar: number;
  /**
   * The live bills' chargeable plates by state, summed — the served dots — or
   * `null` when the API does not send them.
   */
  plates: FloorPlateCounts | null;
  /**
   * Audit round 2, Phase 3 — a PREPARING plate on one of the live bills is
   * over its own preparing target. The SERVER's answer (`latePreparing`, the
   * kitchen board's rule judged at the read), OR-ed across the bills; never
   * computed here and never re-judged by a clock — it changes on the next
   * read of the floor. False against an API that does not send it.
   */
  late: boolean;
  /**
   * THE PRE-BILL CHECK, when it is what the table is about (round 5) — or
   * `null`, and the tile leads with `status` as before.
   *
   * Only while the table is quiet: a live bill, nothing waiting on staff and
   * nothing the kitchen still holds. A table with food on the way is about the
   * food; once it is all out, a check on every live bill makes the check the
   * table's state.
   *
   * - `printed` — every live bill has a check and each still matches its
   *   bill (amber: the guests are deciding how to pay).
   * - `stale` — at least one bill moved after its check (red: reprint it).
   *   Any stale bill makes the table stale, because that paper is wrong.
   *
   * A table where only some bills have a check, all of them still right, has
   * no check state: the others are still just open.
   */
  check: FloorTileCheck | null;
}

export interface FloorTileCheck {
  state: "printed" | "stale";
  /** The latest check on the table. */
  at: string;
}

/**
 * The check state of a quiet table, per the rule on `FloorTileFacts.check`.
 * Exported for the tests; the tile reads it through `floorTileFacts`.
 */
export function tableCheckState(
  openInvoices: readonly OpenInvoice[],
): FloorTileCheck | null {
  if (openInvoices.length === 0) return null;
  const quiet = openInvoices.every(
    (invoice) =>
      invoice.pendingItemCount === 0 && (invoice.kitchenItemCount ?? 0) === 0,
  );
  if (!quiet) return null;
  let anyStale = false;
  let allFresh = true;
  let latest: string | null = null;
  for (const invoice of openInvoices) {
    const freshness = checkFreshness(invoice.lastCheck, invoice.runningTotal);
    if (freshness === "stale") anyStale = true;
    if (freshness !== "fresh") allFresh = false;
    const at = invoice.lastCheck?.at ?? null;
    if (freshness && at && (latest === null || at > latest)) latest = at;
  }
  if (latest === null) return null;
  if (anyStale) return { state: "stale", at: latest };
  return allFresh ? { state: "printed", at: latest } : null;
}

export function floorTileFacts(table: TableGridEntry): FloorTileFacts {
  const openInvoices = table.openInvoices;
  const open = openInvoices[0] ?? null;
  const cheque = table.closedInvoices[0] ?? null;
  const pendingCount = openInvoices.reduce(
    (sum, invoice) => sum + invoice.pendingItemCount,
    0,
  );
  const oldestOpenedAt = openInvoices.reduce<string | null>(
    (oldest, invoice) =>
      oldest === null || invoice.openedAt < oldest ? invoice.openedAt : oldest,
    null,
  );
  const plates = sumPlates(openInvoices);
  const kitchenCount = openInvoices.reduce(
    (sum, invoice) => sum + (invoice.kitchenItemCount ?? 0),
    0,
  );
  const status: FloorTileStatus = open
    ? liveStatus(pendingCount, plates, kitchenCount, false)
    : cheque
      ? "cheque"
      : "free";
  const due = open
    ? openInvoices.reduce(
        (sum, invoice) => sum + (invoice.amountDue ?? invoice.runningTotal),
        0,
      )
    : table.closedInvoices.reduce(
        (sum, row) => sum + (row.amountDue ?? row.total),
        0,
      );
  const paidSoFar = open
    ? openInvoices.reduce((sum, invoice) => sum + (invoice.paidSoFar ?? 0), 0)
    : table.closedInvoices.reduce((sum, row) => sum + (row.paidSoFar ?? 0), 0);
  return {
    status,
    open,
    openBillCount: openInvoices.length,
    openRunningTotal: openInvoices.reduce(
      (sum, invoice) => sum + invoice.runningTotal,
      0,
    ),
    pendingCount,
    selfSeated: openInvoices.some((invoice) => invoice.selfSeated),
    hasPaymentClaim: openInvoices.some(
      (invoice) => invoice.hasPendingPaymentClaim,
    ),
    cashierRequested: openInvoices.some((invoice) =>
      Boolean(invoice.cashierRequestedAt),
    ),
    noCaptain: openInvoices.some((invoice) => invoice.hasCaptain === false),
    cheque,
    chequeCount: table.closedInvoices.length,
    chequeTotal: table.closedInvoices.reduce((sum, row) => sum + row.total, 0),
    oldestOpenedAt,
    due,
    paidSoFar,
    plates,
    late: openInvoices.some((invoice) => invoice.latePreparing === true),
    // A quiet live table (nothing waiting on staff, nothing in the kitchen —
    // `tableCheckState` checks the second) may lead with its check.
    check: open && pendingCount === 0 ? tableCheckState(openInvoices) : null,
  };
}

/**
 * The live status, by the precedence on `FloorTileStatus`: on a dine-in table
 * food on the pass is called READY while more cooks; off-premise (a bag that
 * leaves whole) anything still queued or cooking keeps it PREPARING (B4).
 * Exported for the tests.
 */
export function liveStatus(
  pendingCount: number,
  plates: FloorPlateCounts | null,
  kitchenCount: number,
  offPremise: boolean,
): FloorTileStatus {
  if (pendingCount > 0) return "waiting";
  if (!plates) return kitchenCount > 0 ? "preparing" : "open";
  const cooking = plates.queued + plates.preparing > 0;
  if (offPremise && cooking) return "preparing";
  if (plates.ready > 0) return "ready";
  if (cooking) return "preparing";
  if (plates.served > 0) return "eating";
  return "open";
}

/** The bills' plate counts summed, or `null` if any bill lacks them. */
function sumPlates(
  invoices: readonly OpenInvoice[],
): FloorPlateCounts | null {
  if (invoices.length === 0 || invoices.some((invoice) => !invoice.plates)) {
    return null;
  }
  return invoices.reduce<FloorPlateCounts>(
    (sum, invoice) => ({
      queued: sum.queued + invoice.plates!.queued,
      preparing: sum.preparing + invoice.plates!.preparing,
      ready: sum.ready + invoice.plates!.ready,
      served: sum.served + invoice.plates!.served,
      chargeable: sum.chargeable + invoice.plates!.chargeable,
    }),
    { queued: 0, preparing: 0, ready: 0, served: 0, chargeable: 0 },
  );
}

/** Busy = anything on the table: a live bill or a cheque out. */
export function isBusyTable(table: TableGridEntry): boolean {
  return table.openInvoices.length > 0 || table.closedInvoices.length > 0;
}

export function busyCount(tables: readonly TableGridEntry[]): number {
  return tables.filter(isBusyTable).length;
}

export function allTables(areas: readonly TableGridArea[]): TableGridEntry[] {
  return areas.flatMap((area) => area.tables);
}

/**
 * How long the table has been seated, as the ring around its age.
 *
 * The ring fills toward 90 minutes and turns amber at 45 and red at 75 — the
 * redesign's thresholds. They are a glance cue for "new vs stuck", not a rule:
 * nothing in the product acts on them.
 */
export type TableAgeTone = "ok" | "warn" | "late";

export interface TableAge {
  minutes: number;
  /** 0–100, how much of the ring is filled. */
  percent: number;
  tone: TableAgeTone;
}

export const TABLE_AGE_FULL_MINUTES = 90;
export const TABLE_AGE_WARN_MINUTES = 45;
export const TABLE_AGE_LATE_MINUTES = 75;

export function tableAge(openedAt: string, now: number): TableAge {
  const minutes = Math.max(
    0,
    Math.floor((now - new Date(openedAt).getTime()) / 60000),
  );
  return {
    minutes,
    percent: Math.min(100, Math.round((minutes / TABLE_AGE_FULL_MINUTES) * 100)),
    tone:
      minutes < TABLE_AGE_WARN_MINUTES
        ? "ok"
        : minutes < TABLE_AGE_LATE_MINUTES
          ? "warn"
          : "late",
  };
}

/**
 * THE FLOOR CARD (2026-10 redesign, the "Floor (الصالة)" board).
 *
 * One coloured line per table saying what is happening, and how long it has
 * been happening; the card's action word says what to do next. In order, the
 * first that holds wins:
 *
 * 1. `waiting` — a guest's order from the QR awaits acceptance («راجع الطلب»).
 * 2. `ready` — food on the pass («قدّم»), timed from the plate that has waited
 *    longest there (`readyWaitingSince`).
 * 3. `stale` — the printed bill no longer matches it («اطبع الحساب»). Always
 *    marked late: that paper is wrong now.
 * 4. `bill` — the bill is with the guests: a fresh pre-bill check on a quiet
 *    table, or a cheque out with no live bill («تحصيل»).
 * 5. `prebill` — a guest asked for the bill from the QR (D132 `BILL`), and it
 *    is not printed yet («اطبع الحساب»).
 * 6. `kitchen` — plates queued or cooking; late only by the SERVER's preparing
 *    rule (`latePreparing`), never by a clock here.
 * 7. `eating` — everything has gone out. Calm, never late.
 * 8. `seated` — a live bill with nothing sent yet («خذ الطلب»).
 *
 * TIMERS STAY NEUTRAL UNTIL A STATE PASSES ITS LIMIT (`FLOOR_CARD_LIMITS`),
 * then the card gets a red ring. Those limits are judged on this device's
 * minute clock, unlike the kitchen's late rule: they are a floor cue for "go
 * now", and nothing in the product acts on them.
 */
export type FloorCardKind =
  | "waiting"
  | "ready"
  | "stale"
  | "bill"
  | "prebill"
  | "kitchen"
  | "eating"
  | "seated"
  | "free";

/** The "تحتاجك الآن" bar's groups; `eating` needs nobody and is not counted. */
export type FloorCardGroup =
  | "ready"
  | "bill"
  | "seated"
  | "kitchen"
  | "eating"
  | "free";

/** Minutes a state may last before its card turns late. */
export const FLOOR_CARD_LIMITS: Partial<Record<FloorCardKind, number>> = {
  waiting: 5,
  ready: 5,
  bill: 8,
  prebill: 6,
  seated: 5,
};

const CARD_GROUP: Record<FloorCardKind, FloorCardGroup> = {
  waiting: "seated",
  ready: "ready",
  stale: "bill",
  bill: "bill",
  prebill: "bill",
  kitchen: "kitchen",
  eating: "eating",
  seated: "seated",
  free: "free",
};

export interface FloorCard {
  kind: FloorCardKind;
  group: FloorCardGroup;
  /** Minutes in this state, or null when the read carries no moment for it. */
  minutes: number | null;
  late: boolean;
}

function earliest(values: readonly (string | null | undefined)[]): string | null {
  let first: string | null = null;
  for (const value of values) {
    if (value && (first === null || value < first)) first = value;
  }
  return first;
}

/** Whether any open ask on the table is the guest asking for the bill. */
function billAskedAt(
  asks: readonly { kind: string; requestedAt: string }[] | undefined,
): string | null {
  return earliest((asks ?? []).filter((ask) => ask.kind === "BILL").map((ask) => ask.requestedAt));
}

export function floorCard(
  table: TableGridEntry,
  facts: FloorTileFacts,
  asks: readonly { kind: string; requestedAt: string }[] | undefined,
  now: number,
): FloorCard {
  const bills = table.openInvoices;
  let kind: FloorCardKind;
  let since: string | null = null;
  if (!facts.open) {
    kind = facts.cheque ? "bill" : "free";
    since = facts.cheque?.closedAt ?? null;
  } else if (facts.status === "waiting") {
    kind = "waiting";
    since = facts.oldestOpenedAt;
  } else if (facts.status === "ready") {
    kind = "ready";
    since = earliest(bills.map((bill) => bill.readyWaitingSince));
  } else if (facts.check) {
    kind = facts.check.state === "stale" ? "stale" : "bill";
    since = facts.check.at;
  } else if (billAskedAt(asks)) {
    kind = "prebill";
    since = billAskedAt(asks);
  } else if (facts.status === "preparing") {
    kind = "kitchen";
    since = earliest(bills.map((bill) => bill.preparingSince));
  } else if (facts.status === "eating") {
    kind = "eating";
    since = facts.oldestOpenedAt;
  } else {
    kind = "seated";
    since = facts.oldestOpenedAt;
  }
  const minutes = since === null ? null : tableAge(since, now).minutes;
  const limit = FLOOR_CARD_LIMITS[kind];
  const late =
    kind === "stale" ||
    (kind === "kitchen" && facts.late) ||
    (limit !== undefined && minutes !== null && minutes >= limit);
  return { kind, group: CARD_GROUP[kind], minutes, late };
}
